import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard,
  MessageSquare, 
  Users, 
  Settings, 
  Zap, 
  BarChart2,
  AppWindow,
  UserCircle,
  BookOpen,
  Home,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  Paintbrush,
  ToggleLeft,
  Globe,
  Bot,
  Inbox,
  Cpu,
  Calendar,
  Lock
} from 'lucide-react';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useChatbot } from '../../contexts/ChatbotContext';
import { MiniAppId } from '../../utils/miniAppsIcons';

// Structure des éléments du menu principal
const menuItems = [
  { icon: LayoutDashboard, label: 'Overview', path: '/app/dashboard' },
  { icon: MessageSquare, label: 'Conversations', path: '/app/conversations' },
  { icon: Users, label: 'Leads', path: '/app/visitors' },
  { icon: BarChart2, label: 'Statistics', path: '/app/statistics' },
  { icon: Zap, label: 'Integrations', path: '/app/integrations' },
  { 
    icon: Settings, 
    label: 'Settings', 
    path: '/app/settings', 
    hasSubmenu: true,
    submenu: [
      { icon: Cpu, label: 'AI Settings', path: '/app/settings/ai' },
      { icon: Globe, label: 'Multilingual', path: '/app/settings/multilingual' },
      { icon: Paintbrush, label: 'Design', path: '/app/settings/design' },
      { icon: ToggleLeft, label: 'Widget', path: '/app/settings/widget' },
      { icon: Lock, label: 'Chatbot Limits', path: '/app/settings/limits' },
    ]
  },
  { icon: UserCircle, label: 'Account', path: '/app/settings/profiles' },
  { icon: BookOpen, label: 'Instructions', path: '/app/settings/general' },
  { icon: Home, label: 'Home', path: '/' },
];

export function MobileMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const location = useLocation();
  const { chatbotPublicId } = useChatbot();
  const { miniApps, initialized, initializeMiniApps } = useMiniApps();
  const [miniAppItems, setMiniAppItems] = useState<any[]>([]);

  // Initialiser les mini-apps
  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initializeMiniApps(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initializeMiniApps]);

  // Créer les éléments du menu des mini-apps
  useEffect(() => {
    // Filtrer les mini-apps activées
    const enabledMiniApps = miniApps.filter(app => app.enabled);
    
    // Créer les éléments de sous-menu pour les mini-apps
    const miniAppSubmenuItems = enabledMiniApps.map(app => ({
      icon: app.icon,
      label: app.title.replace('Mini App ', ''),
      path: `/app/settings/${app.id === 'calendar' ? 'calendar' : app.id}`
    }));
    
    // Mettre à jour l'état
    setMiniAppItems(miniAppSubmenuItems);
  }, [miniApps]);

  // Mettre à jour le menu des mini-apps
  const menuItemsWithMiniApps = [...menuItems];
  
  // Trouver l'index de l'élément Mini Apps
  const miniAppsIndex = menuItemsWithMiniApps.findIndex(item => 
    item.hasSubmenu && item.path === '/app/settings/mini-apps'
  );
  
  if (miniAppsIndex === -1) {
    // Si Mini Apps n'existe pas dans le menu, l'ajouter
    menuItemsWithMiniApps.splice(5, 0, { 
      icon: AppWindow, 
      label: 'Mini Apps', 
      path: '/app/settings/mini-apps',
      hasSubmenu: true,
      submenu: [
        { icon: AppWindow, label: 'Overview', path: '/app/settings/mini-apps' },
        ...(miniAppItems.length > 0 ? miniAppItems : [])
      ]
    });
  } else if (miniAppItems.length > 0) {
    // Si Mini Apps existe, mettre à jour son sous-menu
    menuItemsWithMiniApps[miniAppsIndex].submenu = [
      { icon: AppWindow, label: 'Overview', path: '/app/settings/mini-apps' },
      ...miniAppItems
    ];
  }

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Fermer le sous-menu quand on ferme le menu principal
    if (isOpen) {
      setActiveSubmenu(null);
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  const toggleSubmenu = (path: string) => {
    setActiveSubmenu(activeSubmenu === path ? null : path);
  };

  const handleSubmenuItemClick = () => {
    // Fermer le menu et sous-menu lors du clic sur un élément du sous-menu
    closeMenu();
  };

  return (
    <>
      {/* Bouton de menu hamburger - déplacé à droite */}
      <button
        onClick={toggleMenu}
        className="fixed top-20 right-4 z-50 flex items-center justify-center w-10 h-10 bg-black/30 backdrop-blur-sm border border-gray-700/30 text-gray-300 rounded-md shadow-sm hover:bg-gray-900/30 hover:text-white transition-all duration-200"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMenu}
      />

      {/* Menu slide-in - maintenant depuis la droite */}
      <div
        className={`fixed right-0 top-0 w-64 h-full bg-gradient-to-bl from-gray-900 via-gray-800 to-black z-50 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } overflow-hidden`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
          <span className="text-white text-lg font-medium">Menu</span>
          <button
            onClick={closeMenu}
            className="text-gray-400 hover:text-white p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="h-[calc(100%-4rem)] overflow-y-auto py-4">
          <ul>
            {menuItemsWithMiniApps.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              const isSubmenuActive = activeSubmenu === item.path;
              
              return (
                <li key={item.path} className="relative">
                  {item.hasSubmenu ? (
                    <button
                      onClick={() => toggleSubmenu(item.path)}
                      className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                        isActive || isSubmenuActive
                          ? 'bg-gradient-to-l from-blue-600/20 to-indigo-600/20 border-r-2 border-indigo-500 text-white'
                          : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon
                          className={`w-5 h-5 ${
                            isActive || isSubmenuActive ? 'text-blue-400' : 'text-gray-400'
                          }`}
                        />
                        <span>{item.label}</span>
                      </div>
                      <ChevronRight className={`w-4 h-4 transition-transform ${isSubmenuActive ? 'rotate-90' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                        isActive
                          ? 'bg-gradient-to-l from-blue-600/20 to-indigo-600/20 border-r-2 border-indigo-500 text-white'
                          : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }`}
                      onClick={closeMenu}
                    >
                      <item.icon
                        className={`w-5 h-5 ${
                          isActive ? 'text-blue-400' : 'text-gray-400'
                        }`}
                      />
                      <span>{item.label}</span>
                    </Link>
                  )}

                  {/* Sous-menu */}
                  {item.hasSubmenu && (
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isSubmenuActive ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <ul className="bg-gray-800/30 py-1">
                        {item.submenu?.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <li key={subItem.path}>
                              <Link
                                to={subItem.path}
                                className={`flex items-center gap-3 pl-10 pr-4 py-2 transition-colors ${
                                  isSubActive
                                    ? 'bg-gradient-to-l from-blue-600/10 to-indigo-600/10 border-r-2 border-indigo-500/70 text-white'
                                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                }`}
                                onClick={handleSubmenuItemClick}
                              >
                                <subItem.icon
                                  className={`w-4 h-4 ${
                                    isSubActive ? 'text-blue-400' : 'text-gray-400'
                                  }`}
                                />
                                <span className="text-sm">{subItem.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 bg-blue-900/20 backdrop-blur-sm text-xs text-center text-gray-400">
          BotMaker Agency © {new Date().getFullYear()}
        </div>
      </div>
    </>
  );
} 